import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Theme } from '@mui/material/styles';
import Plot from 'react-plotly.js'
import { ThemeConsumer } from 'react-bootstrap/esm/ThemeProvider';
import { e, setPowersetDependencies, trace } from 'mathjs';
import Plotly, { PlotDatum, PlotMouseEvent } from 'plotly.js';
import { debounce } from 'lodash';

interface IPlotProps {
  id: number
  layers: any
  layout: any
  width?: number
  height?: number
  onEdit?: (content: string | number[] | {width: number | undefined, height: number | undefined}) => void;
  isError?: boolean
  errorMessage?: string
  theme?: Theme,
  selectedChannel?: number;
  selectedTimepoint?: string;
  setSelectedChannel?: (channel: number) => void;
  setSelectedTimepoint?: (timepoint: string) => void;
  setPlotRendering: (isRendering: boolean) => void;


}

export default function ResponsivePlot({
  id,
  layers,
  layout,
  width,
  height,
  onEdit,
  isError,
  errorMessage,
  theme,
  selectedChannel,
  selectedTimepoint,
  setSelectedChannel,
  setSelectedTimepoint,
  setPlotRendering
}: IPlotProps) {

  // let { layout, layers, width, height, theme, id } = props;

  let topMargin = 0;
  if (layout.grid !== undefined) {
    if (layout.title?.text !== '') {
      topMargin = 70; // title, tilemode
    } else {
      topMargin = 50; // no title, tilemode
    }
  } else if (layout.title?.text !== '') {
    topMargin = 50; // title, not tilemode
  } else {
    topMargin = 0; // no title, not tilemode
  }

  const [bump, setBump] = useState<boolean>(true);


  const adjustedLayout = useMemo(()=> { return {
    ...layout,
    // width: width ? width - 10 : layout.width,
    // height: heght ? height - 80 : layout.height,
    width: width ? width : layout.width,
    height: height ? height : layout.height,
    // xaxis: {
    //   ...layout.xaxis,
    //   // title: layout.xaxis?.title  ? (layout.xaxis.title !== '' ? ("<b>" + layout.xaxis.title + "</b>") : '') : '', 
    //   // ticktext: layout.xaxis?.ticktext ? (layout.xaxis.ticktext.map((label: string) => `<b>${label}</b>`)) : [],
    // },
    // yaxis: {
    //   ...layout.yaxis,
    //   title: layout.yaxis?.title ? (layout.yaxis.title !== '' ? ("<b>" + layout.yaxis.title + "</b>") : '') : '', 
    // },
    font: {
      ...layout.font,
      family: theme ? theme.typography.fontFamily : "Open Sans, sans=serif",
    },
    // title: {
    //   ...layout.title,
    //   text: layout && layout.title && layout.title.text ? (layout.title.text !== '' ? ("<b>" + layout.title.text + "</b>") : '') : '', 
    // },
    margin: {
      l: 75,  // left margin
      r: 25,  // right margin
      t: topMargin,
      b: 0,  // Dynamically adjusted when x_axis > automargin enabled
      pad: 0  // padding between plot area and margin
    },
    'dragmode': 'scale',
    'clickmode': 'event+select'
    // clickmode: 'select'
  }}, [layout, width, height, bump]);

  // Function to wrap existing names in <b> tags
  // const wrapNamesInBold = (data: any) => {
  //   return data.map((trace: any) => ({
  //     ...trace,
  //     name: `<b>${trace.name}</b>`,  // Wrap existing name in <b> tags
  //   }));
  // };

  // Update the data with bold names
  // const adjustedLayers = wrapNamesInBold(layers);

  // const modeBarButtonsToRemove = [
  //   'lasso2d',
  //   'select2d',
  //   'zoomIn2d',
  //   'zoomOut2d',
  //   'autoScale2d',
  // ];

  // LIST OF BUTTONS:
  // -'2D', zoom2d, pan2d, select2d, lasso2d, zoomIn2d, zoomOut2d, autoScale2d, resetScale2d
  // -'3D', zoom3d, pan3d, orbitRotation, tableRotation, handleDrag3d, resetCameraDefault3d, resetCameraLastSave3d, hoverClosest3d
  // -'Cartesian', hoverClosestCartesian, hoverCompareCartesian
  // -'Geo', zoomInGeo, zoomOutGeo, resetGeo, hoverClosestGeo
  // -'Other', hoverClosestGl2d, hoverClosestPie, toggleHover, resetViews, toImage, sendDataToCloud, toggleSpikelines, resetViewMapbox

  const config = {
    displaylogo: false,
    displayModeBar: true,
    editable: true,
    annotationPosition: true, 
    annotationText: true,
    annotationTail: true,
    axisTitleText: false,
    colorbarPosition: true,
    colorbarTitleText: false,
    legendPosition: true,
    legendText: false,
    shapePosition: true, 
    titleText: false,
    responsive: true
    // modeBarButtonsToRemove: modeBarButtonsToRemove,
  };

  const isLayersEmpty = layers.length === 0;

  const [selected, setSelected] = useState<PlotDatum[]>([]);



  const handlePlotlyClick = useCallback((points: any[]) => {
    if (setSelectedChannel && setSelectedTimepoint) {
      if (points.length > 0 && points[0].hovertext !== undefined) {
        const hoverText = points[0].hovertext;
        if (hoverText.includes('Channel:') && hoverText.includes('Timepoint:')) {
          const selectedChannelId = parseInt(hoverText.split('Channel: ')[1].split('<')[0]);
          const selectedTimepoint = hoverText.split('Timepoint: ')[1].split('<')[0];
          // console.log('Selected Channel: ', selectedChannelId);
          setSelectedChannel(selectedChannelId);
          setSelectedTimepoint(selectedTimepoint);
        }
      }
    }
  }, [setSelectedChannel, setSelectedTimepoint])


  useEffect(()=> {
    handlePlotlyClick(selected)
    if (bump){
      setBump(!bump)
    }
  }, [selected])

  useEffect(() => {
    let hasSelected = false
    layers.forEach((layer: any) => {
      if (layer.hovertext === undefined) {
        return;
      }
      const selectedPointsBoolArr: boolean[] = layer.hovertext.map((ht: string) => {
        if (ht.includes('Channel:') && ht.includes('Timepoint:')) {
          const containsCorrectChannel = parseInt(ht.split('Channel:')[1].split('<br')[0].trim()) === selectedChannel
          const containsCorrectTimepoint = ht.split('Timepoint:')[1].split('<br')[0].trim() === selectedTimepoint
          return containsCorrectChannel && containsCorrectTimepoint
        }
        else {
          return false
        }
      })
      const selectedPointsArr: number[] = selectedPointsBoolArr.reduce((indices, value, index) => {
        if (value) {
          indices.push(index);
        }
        return indices;
      }, [] as number[]);
      if (selectedPointsArr.length > 0) {
        hasSelected = true;
      }
      layer.selectedpoints = selectedPointsArr
    });
    if (!hasSelected){
      layers.forEach((layer: any) => {
        if (layer.selectedpoints === undefined) {
          return;
        }
        if (layer.selectedpoints.length === 0) {
          layer.selectedpoints = undefined
        }
      });
    }

    setBump(false)
  }, [selectedTimepoint, selectedChannel])


  const setPlotRenderingDebounced = useCallback((rendering: boolean) => {
    return debounce(() => {
      setPlotRendering(rendering);
    }, 100);
  }, [setPlotRendering]);

  let returnObject = <Plot
                        data={layers}
                        layout={adjustedLayout}
                        config={config}
                        onAfterPlot={() => {
                          // console.log("Done Render");
                          setPlotRenderingDebounced(false)
                        }}
                        onSelecting={(e) => setSelected(e.points)}
                        onClick={(e) => { setSelected(e.points);}}
                      />;

  if (isError) {
    returnObject =  
    <div style={{ textAlign: 'center' }}>
        <p style={{ 
          // textTransform: 'uppercase', 
          color: '#C93030', // This is a medium grey, adjust as needed
          fontSize: '1rem', // Adjust font size as desired
          fontWeight: '' // If you want the text to be bold
        }}>
          {errorMessage}
        </p>
    </div>
  } else if (isLayersEmpty) {
    returnObject =  
        <div style={{ textAlign: 'center' }}>
            <p style={{ 
              // textTransform: 'uppercase', 
              color: '#808080', // This is a medium grey, adjust as needed
              fontSize: '1rem', // Adjust font size as desired
              fontWeight: '' // If you want the text to be bold
            }}>
              No Data Selected
            </p>
        </div>
  }
   
  return (
    <div style={{ display: 'flex', height: '100%', justifyContent: 'center', alignItems: 'center'}} data-plot-id={id} className="plot-container" >
      <div className="drag-handle" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '20px', cursor: 'move' }}></div>
      {returnObject}
    </div>
  );
  
};

